/**
 * Fonts available to menus. Served from Bunny Fonts (a GDPR-friendly, no-tracking Google Fonts
 * mirror) so diners' IPs are not sent to Google.
 */
export const FONT_CATALOG: Array<{ family: string; category: "serif" | "sans" | "display"; weights: number[]; italics?: number[] }> = [
  { family: "Inter", category: "sans", weights: [400, 500, 600, 700] },
  { family: "DM Sans", category: "sans", weights: [400, 500, 700] },
  { family: "Work Sans", category: "sans", weights: [400, 500, 600, 700] },
  { family: "Poppins", category: "sans", weights: [400, 500, 600, 700] },
  { family: "Montserrat", category: "sans", weights: [400, 500, 600, 700, 800] },
  { family: "Raleway", category: "sans", weights: [400, 500, 600, 700] },
  { family: "Nunito", category: "sans", weights: [400, 600, 700] },
  { family: "Roboto", category: "sans", weights: [400, 500, 700] },
  { family: "Open Sans", category: "sans", weights: [400, 600, 700] },
  { family: "Rubik", category: "sans", weights: [400, 500, 700] },
  { family: "Josefin Sans", category: "sans", weights: [400, 600, 700] },
  { family: "Space Grotesk", category: "sans", weights: [400, 500, 700] },
  { family: "Playfair Display", category: "serif", weights: [400, 500, 600, 700], italics: [400, 500] },
  { family: "Lora", category: "serif", weights: [400, 500, 600, 700], italics: [400] },
  { family: "Merriweather", category: "serif", weights: [400, 700], italics: [400] },
  { family: "Cormorant Garamond", category: "serif", weights: [400, 500, 600, 700], italics: [400, 500] },
  { family: "EB Garamond", category: "serif", weights: [400, 500, 600, 700], italics: [400] },
  { family: "Libre Baskerville", category: "serif", weights: [400, 700], italics: [400] },
  { family: "Source Serif 4", category: "serif", weights: [400, 600, 700], italics: [400] },
  { family: "Fraunces", category: "serif", weights: [400, 500, 600, 700], italics: [400, 500] },
  { family: "DM Serif Display", category: "display", weights: [400], italics: [400] },
  { family: "Oswald", category: "display", weights: [400, 500, 600, 700] },
  { family: "Bebas Neue", category: "display", weights: [400] },
  { family: "Archivo Black", category: "display", weights: [400] },
];

const byLower = new Map(FONT_CATALOG.map((f) => [f.family.toLowerCase(), f]));

export function isKnownFont(family: string): boolean {
  return byLower.has(family.toLowerCase());
}

/** Map an arbitrary font-family stack (e.g. from computed CSS) to the closest catalog font. */
export function closestFont(stack: string, fallback: "serif" | "sans" = "sans"): string {
  const families = stack
    .split(",")
    .map((s) => s.trim().replace(/^["']|["']$/g, ""))
    .filter(Boolean);
  for (const f of families) {
    const hit = byLower.get(f.toLowerCase());
    if (hit) return hit.family;
  }
  const lower = stack.toLowerCase();
  if (/garamond/.test(lower)) return "EB Garamond";
  if (/baskerville/.test(lower)) return "Libre Baskerville";
  if (/(didot|bodoni|playfair)/.test(lower)) return "Playfair Display";
  if (/(condensed|oswald|impact|league gothic|anton)/.test(lower)) return "Oswald";
  if (/(futura|avenir|gotham|montserrat|proxima)/.test(lower)) return "Montserrat";
  if (/(helvetica|arial|roboto|system-ui|segoe|sans-serif|sans)/.test(lower)) return "Inter";
  if (/(times|georgia|serif)/.test(lower)) return "Lora";
  return fallback === "serif" ? "Lora" : "Inter";
}

export function fontStylesheetUrl(families: string[]): string | null {
  const unique = [...new Set(families)].filter(isKnownFont);
  if (unique.length === 0) return null;
  const parts = unique.map((family) => {
    const f = byLower.get(family.toLowerCase())!;
    // Bunny Fonts syntax: "400,500,400i" (real italics instead of browser-faked slants).
    return `${f.family.toLowerCase().replace(/ /g, "-")}:${[...f.weights, ...(f.italics ?? []).map((w) => `${w}i`)].join(",")}`;
  });
  return `https://fonts.bunny.net/css?family=${parts.join("|")}&display=swap`;
}

export function fontStack(family: string): string {
  const f = byLower.get(family.toLowerCase());
  const generic = f?.category === "serif" ? "Georgia, 'Times New Roman', serif" : "system-ui, -apple-system, 'Segoe UI', sans-serif";
  return `'${family.replace(/'/g, "")}', ${generic}`;
}
